import { useMemo } from "react";
import {
  ResponsiveContainer,
  ScatterChart,
  Scatter,
  ReferenceLine,
  XAxis,
  YAxis,
  Tooltip as RechartsTooltip,
} from "recharts";
import type { TooltipProps } from "recharts";
import { ScatterNode, BaseScatterPoint, SCATTER_POINT_SIZE } from "./shared/ScatterPlotShared";

export interface ConstantSumScatterPoint extends BaseScatterPoint {
  xValue: number;
  yValue: number;
  averageValue: number;
  userValue: number;
  parentLabel?: string;
  description?: string;
}

interface ConstantSumScatterPlotProps {
  data: ConstantSumScatterPoint[];
  xLabel: string;
  yLabel: string;
  maxValue: number;
}

const ScatterTooltip = ({ active, payload }: TooltipProps<number, string>) => {
  if (!active || !payload?.length || !payload[0]?.payload) return null;
  const point = payload[0].payload as ConstantSumScatterPoint;
  const overUnder = point.userValue - point.averageValue;
  const differenceLabel = overUnder === 0 ? "Aligned with average" : `${overUnder > 0 ? "+" : ""}${overUnder.toFixed(1)} vs average`;

  return (
    <div className="rounded-lg border bg-background p-3 shadow-md text-sm space-y-1 max-w-xs">
      <p className="font-semibold leading-tight">{point.label}</p>
      {point.parentLabel && <p className="text-muted-foreground">Cluster: {point.parentLabel}</p>}
      {point.description && <p className="text-muted-foreground text-xs">{point.description}</p>}
      <p className="text-muted-foreground">Your allocation: {point.userValue.toFixed(1)}</p>
      <p className="text-muted-foreground">Average allocation: {point.averageValue.toFixed(1)}</p>
      <p className="text-muted-foreground">{differenceLabel}</p>
    </div>
  );
};

const buildTicks = (maxValue: number) => {
  const step = maxValue > 80 ? 20 : maxValue > 40 ? 10 : 5;
  const roundedMax = Math.ceil(maxValue / step) * step;
  const ticks: number[] = [];
  for (let tick = 0; tick <= roundedMax; tick += step) {
    ticks.push(Number(tick.toFixed(2)));
  }
  if (!ticks.includes(roundedMax)) ticks.push(roundedMax);
  return { ticks, domainMax: roundedMax };
};

export const ConstantSumScatterPlot = ({ data, xLabel, yLabel, maxValue }: ConstantSumScatterPlotProps) => {
  const { ticks, domainMax } = useMemo(() => buildTicks(maxValue), [maxValue]);
  const pointSize = SCATTER_POINT_SIZE;

  return (
    <div className="w-full h-[420px]">
      <ResponsiveContainer width="100%" height="100%">
        <ScatterChart margin={{ top: 52, right: 48, bottom: 64, left: 72 }}>
          <XAxis
            type="number"
            dataKey="xValue"
            domain={[0, domainMax]}
            ticks={ticks}
            label={{ value: xLabel, position: "insideBottom", offset: -45, fill: "#475569" }}
            padding={{ left: 10, right: 10 }}
          />
          <YAxis
            type="number"
            dataKey="yValue"
            domain={[0, domainMax]}
            ticks={ticks}
            label={{
              value: yLabel,
              position: "insideLeft",
              angle: -90,
              offset: -10,
              fill: "#475569",
              style: { textAnchor: "middle" },
            }}
            padding={{ top: 10, bottom: 16 }}
          />
          <ReferenceLine
            segment={[
              { x: 0, y: 0 },
              { x: domainMax, y: domainMax },
            ]}
            stroke="#94a3b8"
            strokeWidth={2}
            strokeDasharray="6 6"
          />
          <RechartsTooltip content={<ScatterTooltip />} cursor={{ strokeDasharray: "3 3" }} />
          <Scatter data={data} shape={(props) => <ScatterNode {...props} size={pointSize} />} />
        </ScatterChart>
      </ResponsiveContainer>
    </div>
  );
};
